/**
 * Batch 18-I7 — Provider Evidence Summary Hook.
 *
 * Thin React-Query wrapper over the I5 derived summary RPC
 * (`public.get_training_provider_evidence_summary(uuid, uuid[])`).
 * Returns one row per (provider, artifact, technician) tuple where
 * matched provider evidence exists in the I3 raw event table. Treats
 * any `provider_state` value not in the locked closed set as `none`
 * defensively.
 *
 * Trust contract (locked from I1 / I2 / I5 / I6):
 *   - This hook is read-only and emits NO user-facing display labels.
 *     Mapping `provider_state` to a badge label is the I8 helper's
 *     job, not this file's.
 *   - The hook NEVER queries `training_completion_verifications`,
 *     `training_consumption_sessions`,
 *     `training_session_extension_evidence`, or the raw
 *     `training_provider_evidence_events` table directly. Only the
 *     I5 derived summary RPC.
 *   - Provider state is a PARALLEL state to the existing manager /
 *     manual evidence union (G4 / G6 / G14 / G16 / H2). It must
 *     never be flattened into the manager-attested or
 *     manual-submitted unions by any consumer of this hook.
 *   - Surface-wiring is deferred to I9+. I7 is a hook-only batch.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ─── Public types ───────────────────────────────────────────────────

export type ProviderEvidenceState =
  | 'none'
  | 'attested'
  | 'revoked'
  | 'superseded'
  | 'pending';

export interface TrainingProviderEvidenceSummaryRow {
  org_id: string;
  shop_id: string | null;
  provider_id: string;
  matched_technician_profile_id: string;
  matched_user_id: string | null;
  matched_artifact_id: string;
  provider_state: ProviderEvidenceState;
  latest_event_id: string;
  latest_attested_at: string | null;
  latest_received_at: string;
  provider_display_name: string;
  provider_key: string;
  external_course_id: string | null;
  external_user_id: string | null;
  reconciliation_status: string | null;
}

export interface UseTrainingProviderEvidenceSummaryOptions {
  technicianProfileId?: string | null;
  artifactIds?: string[] | null;
  enabled?: boolean;
}

// ─── Constants used by the closed-set defensive normalizer ──────────

const ALLOWED_STATES = new Set<ProviderEvidenceState>([
  'none',
  'attested',
  'revoked',
  'superseded',
  'pending',
]);

// ─── Internal: stable artifact-id normalization ─────────────────────
// Dedupes + sorts so the React-Query cache key is order-independent
// (callers passing the same set in different orders share a cache
// entry). Empty input → empty output.
function normalizeArtifactIds(input: string[] | null | undefined): string[] {
  if (!input || input.length === 0) return [];
  const set = new Set<string>();
  for (const id of input) {
    if (typeof id === 'string' && id.length > 0) set.add(id);
  }
  return Array.from(set).sort();
}

// ─── Internal: cast the strongly-typed Supabase client to accept the
//     I5 RPC name. The repo's generated Supabase types do not yet
//     know about `get_training_provider_evidence_summary`; this cast
//     is local to the hook and easy to remove once types are
//     regenerated.
// ────────────────────────────────────────────────────────────────────
type ProviderEvidenceSummaryRpc = (
  fn: 'get_training_provider_evidence_summary',
  args: {
    p_technician_profile_id: string;
    p_artifact_ids: string[];
  },
) => Promise<{
  data: TrainingProviderEvidenceSummaryRow[] | null;
  error: { message: string } | null;
}>;

// ─── Public hook ────────────────────────────────────────────────────

export function useTrainingProviderEvidenceSummary(
  options: UseTrainingProviderEvidenceSummaryOptions,
): UseQueryResult<TrainingProviderEvidenceSummaryRow[], Error> {
  const technicianProfileId = options.technicianProfileId ?? null;
  const normalizedArtifactIds = normalizeArtifactIds(options.artifactIds);

  const callerEnabled = options.enabled !== false;
  const enabled =
    callerEnabled &&
    typeof technicianProfileId === 'string' &&
    technicianProfileId.length > 0 &&
    normalizedArtifactIds.length > 0;

  return useQuery<TrainingProviderEvidenceSummaryRow[], Error>({
    queryKey: [
      'training-provider-evidence-summary',
      technicianProfileId,
      normalizedArtifactIds,
    ],
    enabled,
    queryFn: async (): Promise<TrainingProviderEvidenceSummaryRow[]> => {
      const rpc = supabase.rpc.bind(supabase) as unknown as ProviderEvidenceSummaryRpc;
      const { data, error } = await rpc(
        'get_training_provider_evidence_summary',
        {
          p_technician_profile_id: technicianProfileId as string,
          p_artifact_ids: normalizedArtifactIds,
        },
      );
      if (error) throw new Error(error.message);

      const rows = data ?? [];
      // Defensive normalization: drop any row whose provider_state is
      // not in the closed set. This protects downstream display code
      // from accidentally rendering an unknown state.
      return rows.filter((row) =>
        ALLOWED_STATES.has(row.provider_state),
      );
    },
  });
}
